"use strict";
/**
 * 역할(role) 정의 — 코드·이름·계층·관리자 여부·등록 가능 표.
 *
 * ⚠ 이 파일은 `codes.ts`와 함께 이 패키지에서 **런타임 값**을 내보내는 예외다.
 * 역할은 서버(등록 검증·매트릭스 편집 판정)와 프론트(선택 목록·라벨)가 **같은 정의**를 읽어야
 * 판정이 갈리지 않는다. 프론트에서 값 import가 문제되면(Vite CJS named export)
 * `Record<RoleCode, ...>`로 재정의해 컴파일 타임 동기화만 가져가도 된다.
 *
 * 근거: 클라이언트(페이먼트코리아 박영희 대표) 서면 확정 2026-09-07 — 역할을 계층별
 * 관리자/사용자 2단으로 재편하고, 상급 관리자가 만들 수 있는 역할을 표로 고정한다.
 *
 * 2026-09-07 이전 역할 6종(HQ_OPERATOR·GROUP_ADMIN·GROUP_MANAGER·GROUP_USER 포함)은
 * `RoleRedesign` 마이그레이션에서 새 6종으로 이전한 뒤 삭제했다.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ROLE_GRANTABLE = exports.SUPER_ADMIN_ROLE_CODE = exports.ADMIN_ROLE_CODES = exports.ROLE_ORG_TYPE = exports.ROLE_LEVEL = exports.ROLE_LABEL = exports.ROLE_CODES = void 0;
exports.roleCodeForLevel = roleCodeForLevel;
exports.isSuperAdminRole = isSuperAdminRole;
exports.isAdminRole = isAdminRole;
exports.grantableRoles = grantableRoles;
exports.orgTypeForRole = orgTypeForRole;
/** role_level 오름차순(권한이 센 순) — 화면 선택 목록의 정렬 기준이기도 하다 */
exports.ROLE_CODES = [
    'HQ_SUPER_ADMIN',
    'HQ_ADMIN',
    'DEALER_ADMIN',
    'DEALER_USER',
    'AGENT_ADMIN',
    'AGENT_USER',
];
/** 화면 표기용 역할 이름 — role.role_name 시드와 같은 문자열이어야 한다 */
exports.ROLE_LABEL = {
    HQ_SUPER_ADMIN: '본사총괄관리자',
    HQ_ADMIN: '본사관리자',
    DEALER_ADMIN: '대리점관리자',
    DEALER_USER: '대리점사용자',
    AGENT_ADMIN: 'Agent관리자',
    AGENT_USER: 'Agent사용자',
};
/** role.role_level — 작을수록 상위. 대표 역할 1건 선택(ORDER BY role_level ASC)에 쓴다 */
exports.ROLE_LEVEL = {
    HQ_SUPER_ADMIN: 1,
    HQ_ADMIN: 2,
    DEALER_ADMIN: 3,
    DEALER_USER: 4,
    AGENT_ADMIN: 5,
    AGENT_USER: 6,
};
/** 급 → 역할 코드 역매핑 (급은 6종에 1:1). 표에 없는 급은 null */
function roleCodeForLevel(level) {
    const found = Object.entries(exports.ROLE_LEVEL).find(([, v]) => v === level);
    return found === undefined ? null : found[0];
}
/**
 * 역할 × 소속 계층 정합성 — 역할 하나가 붙을 수 있는 계층은 정확히 하나다.
 * 계층이 어긋난 부여(예: Agent 소속에 HQ_SUPER_ADMIN)를 막지 않으면 그 계정이
 * 본사 보호 대상으로 승격돼 본사가 잠글 수 없게 된다.
 */
exports.ROLE_ORG_TYPE = {
    HQ_SUPER_ADMIN: 'HQ',
    HQ_ADMIN: 'HQ',
    DEALER_ADMIN: 'DEALER',
    DEALER_USER: 'DEALER',
    AGENT_ADMIN: 'AGENT',
    AGENT_USER: 'AGENT',
};
/**
 * **관리자 역할** — 계층 기본 권한 안에서 산하 계정을 만들고 그 권한을 저장할 수 있는 역할.
 *
 * 인가는 계층(org_type) 기본 권한이 상한을 정하고, 이 값이 그 안에서 다시 한 번 좁힌다.
 * 계층만으로 판정하면 같은 대리점 소속이라는 이유로 대리점**사용자**까지 계정을 만들 수 있다.
 */
exports.ADMIN_ROLE_CODES = [
    'HQ_SUPER_ADMIN',
    'HQ_ADMIN',
    'DEALER_ADMIN',
    'AGENT_ADMIN',
];
/**
 * **본사 총괄 관리자** — 보호 대상 최고 권한 역할이자, 권한 매트릭스에서 **계층 기본값 밖의
 * 권한을 부여(grant)** 할 수 있는 유일한 역할이다 (클라이언트 대표 요청 2026-09-08).
 *
 * 다른 관리자는 여전히 자기 계층 기본값 안에서 깎기만 한다 — 부여를 열면 대리점관리자가
 * 산하 계정에 자기 계층에 없는 권한을 만들어 줄 수 있어 계층 상한이 무너진다.
 */
exports.SUPER_ADMIN_ROLE_CODE = 'HQ_SUPER_ADMIN';
/** 총괄관리자인가 — 알 수 없는 코드는 false (fail-closed) */
function isSuperAdminRole(roleCode) {
    return roleCode === exports.SUPER_ADMIN_ROLE_CODE;
}
/**
 * **등록 가능 역할 표** — 어떤 역할이 어떤 역할을 부여할 수 있는가 (클라이언트 확정 2026-09-07).
 *
 * **총괄관리자만 자기 역할을 만들 수 있다.** 나머지 관리자는 동급을 복제할 수 없어
 * 관리자가 무한 증식하지 않는다 — 클라이언트 원문이 "본사 총괄=총괄·관리자 등록 /
 * 대리점·Agent 관리자=동급 관리자 등록 불가"였다. 총괄이 유일하게 후임 총괄을 세울 수
 * 있어야 그 자리가 비었을 때 복구 경로가 남는다.
 *
 * 본사 2종은 총괄관리자만 만들 수 있고, 사용자 역할(DEALER_USER·AGENT_USER)은 빈 배열이라
 * 계정을 만들 수 없다(fail-closed: 표에 없는 역할도 빈 배열로 취급한다).
 */
exports.ROLE_GRANTABLE = {
    HQ_SUPER_ADMIN: [
        'HQ_SUPER_ADMIN',
        'HQ_ADMIN',
        'DEALER_ADMIN',
        'DEALER_USER',
        'AGENT_ADMIN',
        'AGENT_USER',
    ],
    HQ_ADMIN: ['DEALER_ADMIN', 'DEALER_USER', 'AGENT_ADMIN', 'AGENT_USER'],
    DEALER_ADMIN: ['DEALER_USER', 'AGENT_ADMIN', 'AGENT_USER'],
    DEALER_USER: [],
    AGENT_ADMIN: ['AGENT_USER'],
    AGENT_USER: [],
};
/** 알 수 없는 코드는 false (fail-closed) — 역할 미부여 계정도 관리자가 아니다 */
function isAdminRole(roleCode) {
    return (roleCode !== null &&
        roleCode !== undefined &&
        exports.ADMIN_ROLE_CODES.includes(roleCode));
}
/**
 * 아는 역할 코드인가 — `in`이나 첨자 접근은 프로토타입까지 훑어 'toString' 같은 문자열을
 * 역할로 인정한다(값이 Function이라 `.includes` 호출이 500으로 터진다). 자기 키만 본다.
 * 프론트 재정의(`apps/admin/.../codes/role.ts`)의 `isRoleCode`와 같은 판정이다.
 */
function isKnownRoleCode(roleCode) {
    return (roleCode !== null &&
        roleCode !== undefined &&
        Object.prototype.hasOwnProperty.call(exports.ROLE_LABEL, roleCode));
}
/** 그 역할이 부여할 수 있는 역할 목록 — 표에 없는 코드는 빈 배열 (fail-closed) */
function grantableRoles(roleCode) {
    return isKnownRoleCode(roleCode) ? exports.ROLE_GRANTABLE[roleCode] : [];
}
/** 그 역할이 붙을 수 있는 계층 — 표에 없는 코드는 null (fail-closed) */
function orgTypeForRole(roleCode) {
    return isKnownRoleCode(roleCode) ? exports.ROLE_ORG_TYPE[roleCode] : null;
}
